// Profile & Settings page — tabs: Info, Password & Sessions, 2FA,
// Members, Billing.

import { clsx } from "clsx";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/ui/PageHeader";
import { SelectMenu } from "@/components/ui/SelectMenu";
import { BillingTab } from "@/components/profile/BillingTab";
import { MembersTab } from "@/components/profile/MembersTab";
import { ProfileInfoTab } from "@/components/profile/ProfileInfoTab";
import { SecurityTab } from "@/components/profile/SecurityTab";
import { TwoFactorTab } from "@/components/profile/TwoFactorTab";
import { useDict } from "@/i18n/LocaleProvider";

type TabId = "profile" | "security" | "twofa" | "members" | "billing";

export function ProfilePage() {
  const t = useDict().app.profile.page;
  const TABS: Array<{ id: TabId; label: string }> = [
    { id: "profile", label: t.tabs.profile },
    { id: "security", label: t.tabs.security },
    { id: "twofa", label: t.tabs.twofa },
    { id: "members", label: t.tabs.members },
    { id: "billing", label: t.tabs.billing },
  ];
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<TabId>(
    searchParams.get("setup_2fa")
      ? "twofa"
      : searchParams.get("tab") === "billing"
        ? "billing"
        : "profile",
  );

  return (
    <div>
      <PageHeader
        title={t.title}
        subtitle={t.subtitle}
      />

      {/* Mobile: branded SelectMenu (same as engagement detail). */}
      <div className="md:hidden mb-4">
        <SelectMenu<TabId>
          value={tab}
          options={TABS.map((t) => ({ value: t.id, label: t.label }))}
          onChange={setTab}
          ariaLabel={t.sectionAria}
        />
      </div>
      <div className="hidden md:flex gap-[6px] mb-4 border-b border-stroke flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              "px-4 py-[10px] bg-transparent border-none font-mono text-[12px] font-semibold cursor-pointer uppercase tracking-[0.05em] transition-colors",
              tab === t.id ? "text-accent" : "text-muted",
            )}
            style={{
              borderBottom:
                tab === t.id
                  ? "2px solid rgb(var(--color-accent))"
                  : "2px solid transparent",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile" && <ProfileInfoTab />}
      {tab === "security" && <SecurityTab />}
      {tab === "twofa" && <TwoFactorTab />}
      {tab === "members" && <MembersTab />}
      {tab === "billing" && <BillingTab />}
    </div>
  );
}
